import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Box, CircularProgress } from '@mui/material';
import { BrokenImage } from '@mui/icons-material';
import { getApiUrl } from '../config';
import { isRasterImage } from '../utils/uploadUtils';

const Wrapper = styled(Box)({
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
});

const Img = styled('img')({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
});

const FallbackBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    color: theme.palette.text.disabled,
    backgroundColor: theme.palette.action.hover,
}));

const AttachmentImage = ({ attachment, src, alt, loading, onClick, className, style }) => {
    const [url, setUrl] = useState(null);
    const [error, setError] = useState(false);
    const activeUrlRef = useRef(null);

    useEffect(() => {
        if (src || !attachment?._id) {
            setUrl(null);
            setError(false);
            return undefined;
        }
        let cancelled = false;
        setError(false);
        setUrl(null);
        const fetchUrl = `${getApiUrl()}/attachment/${attachment._id}`;
        fetch(fetchUrl, { credentials: 'include' })
            .then((r) => {
                if (!r.ok) throw new Error('Failed to load image');
                return r.blob();
            })
            .then((blob) => {
                if (cancelled) return;
                if (!isRasterImage(blob.type)) throw new Error('Not a raster image');
                const objectUrl = URL.createObjectURL(blob);
                if (activeUrlRef.current) URL.revokeObjectURL(activeUrlRef.current);
                activeUrlRef.current = objectUrl;
                setUrl(objectUrl);
            })
            .catch(() => {
                if (!cancelled) setError(true);
            });

        return () => {
            cancelled = true;
            if (activeUrlRef.current) {
                URL.revokeObjectURL(activeUrlRef.current);
                activeUrlRef.current = null;
            }
        };
    }, [attachment?._id, src]);

    const displaySrc = src || url;

    return (
        <Wrapper className={className} style={style} onClick={onClick}>
            {error && (
                <FallbackBox>
                    <BrokenImage />
                </FallbackBox>
            )}
            {!error && !displaySrc && (
                <CircularProgress size={20} />
            )}
            {!error && displaySrc && (
                <Img
                    src={displaySrc}
                    alt={alt || attachment?.name || 'attachment'}
                    loading={loading}
                    draggable={false}
                    onError={() => setError(true)}
                />
            )}
        </Wrapper>
    );
};

export default AttachmentImage;
